import { renderToString } from "react-dom/server";

import { PinOtherStoreActiveIcon } from "@jti/brand-icons/pin-other-store-active";
import { PinOtherStoreDefaultIcon } from "@jti/brand-icons/pin-other-store-default";
import { PinStoreIcon } from "@jti/brand-icons/pin-store";
import { PinStoreActiveIcon } from "@jti/brand-icons/pin-store-active";
import { PinStoreDefaultIcon } from "@jti/brand-icons/pin-store-default";
import { PinStoreNegativeIcon } from "@jti/brand-icons/pin-store-negative";
import { UiTargetIcon } from "@jti/brand-icons/ui-target";
import type { FlotiqStore, StoreType } from "@jti/fe-common";

import { isBrandStore } from "./utils/isBrandStore";

import type { Theme } from "./types";

const svgDataURIHeader = "data:image/svg+xml;charset=utf-8,";

export const createImageIcon = (icon: string) => {
  return svgDataURIHeader + encodeURIComponent(icon);
};

export const BRAND_STORE_PIN_DARK = createImageIcon(renderToString(<PinStoreNegativeIcon width={30} height={30} />));
export const BRAND_STORE_PIN_LIGHT = createImageIcon(renderToString(<PinStoreDefaultIcon width={30} height={30} />));
export const BRAND_STORE_PIN_ACTIVE = createImageIcon(renderToString(<PinStoreActiveIcon width={30} height={30} />));

export const OTHER_STORE_PIN_DARK = createImageIcon(renderToString(<PinStoreIcon width={30} height={30} />));
export const OTHER_STORE_PIN_LIGHT = createImageIcon(
  renderToString(<PinOtherStoreDefaultIcon width={30} height={30} />)
);
export const OTHER_STORE_PIN_ACTIVE = createImageIcon(
  renderToString(<PinOtherStoreActiveIcon width={30} height={30} />)
);

export const USER_LOCATION = createImageIcon(renderToString(<UiTargetIcon width={43} height={43} fill="#747474ff" />));

export const DEFAULT_MARKER = createImageIcon(renderToString(<PinStoreDefaultIcon width={30} height={30} />));
export const DEFAULT_MARKER_LIGHT = createImageIcon(renderToString(<PinStoreNegativeIcon width={30} height={30} />));
export const DEFAULT_MARKER_NAVER = createImageIcon(renderToString(<PinStoreNegativeIcon width={30} height={30} />));
export const ACTIVE_MARKER = createImageIcon(renderToString(<PinStoreActiveIcon width={30} height={30} />));

type PinIcon = {
  default: string;
  active: string;
};

export const OTHER_STORE_ICON_DARK: PinIcon = {
  default: OTHER_STORE_PIN_DARK,
  active: OTHER_STORE_PIN_ACTIVE,
};
export const OTHER_STORE_ICON_LIGHT: PinIcon = {
  default: OTHER_STORE_PIN_LIGHT,
  active: OTHER_STORE_PIN_ACTIVE,
};

export const BRAND_STORE_ICON_DARK: PinIcon = {
  default: BRAND_STORE_PIN_DARK,
  active: BRAND_STORE_PIN_ACTIVE,
};

export const BRAND_STORE_ICON_LIGHT: PinIcon = {
  default: BRAND_STORE_PIN_LIGHT,
  active: BRAND_STORE_PIN_ACTIVE,
};

const STORE_ICON_MAP: Record<Theme, { [key in StoreType | "default"]?: PinIcon }> = {
  dark: {
    "Flagship Store": BRAND_STORE_ICON_LIGHT,
    "Brand Store": BRAND_STORE_ICON_LIGHT,
    "Pop-up Store": BRAND_STORE_ICON_LIGHT,
    Tobacconist: OTHER_STORE_ICON_LIGHT,
    "Tobacconist with Vending Machine": OTHER_STORE_ICON_LIGHT,
    "Duty Free": OTHER_STORE_ICON_LIGHT,
    "Vending Machine": OTHER_STORE_ICON_LIGHT,
    "Bars/Cafes/Restaurants": OTHER_STORE_ICON_LIGHT,
    default: OTHER_STORE_ICON_LIGHT,
  },
  light: {
    "Flagship Store": BRAND_STORE_ICON_DARK,
    "Brand Store": BRAND_STORE_ICON_DARK,
    "Pop-up Store": BRAND_STORE_ICON_DARK,
    Tobacconist: OTHER_STORE_ICON_DARK,
    "Tobacconist with Vending Machine": OTHER_STORE_ICON_DARK,
    "Duty Free": OTHER_STORE_ICON_DARK,
    "Vending Machine": OTHER_STORE_ICON_DARK,
    "Bars/Cafes/Restaurants": OTHER_STORE_ICON_DARK,
    default: OTHER_STORE_ICON_DARK,
  },
};

type IconFactoryParams = {
  theme?: Theme;
  layoutVersion?: "v1" | "v2";
  defaultStoreIcon?: string;
  activeStoreIcon?: string;
  storeTypeOverrides?: Record<string, string>;
};

export function createStoreIconFactory(params: IconFactoryParams = {}) {
  if (params.layoutVersion === "v2") {
    return getIconForStoreV2(params);
  }

  return getIconForStoreV1(params);
}

export function getIconForStoreV1({
  defaultStoreIcon,
  activeStoreIcon,
  theme,
  storeTypeOverrides = {},
}: IconFactoryParams = {}) {
  return (store: FlotiqStore) => {
    const { storetype } = store;
    const themeIcon = theme === "light" ? DEFAULT_MARKER_LIGHT : DEFAULT_MARKER;
    const defaultIcon = defaultStoreIcon || themeIcon;

    return {
      default: (storetype && storeTypeOverrides[storetype]) || defaultIcon,
      active: activeStoreIcon ?? ACTIVE_MARKER,
    };
  };
}

export function getIconForStoreV2({
  theme,
  defaultStoreIcon,
  activeStoreIcon,
  storeTypeOverrides = {},
}: IconFactoryParams = {}) {
  return (store: FlotiqStore) => {
    const { storetype } = store;

    const themeMap = theme ? (STORE_ICON_MAP[theme] ?? STORE_ICON_MAP.dark) : STORE_ICON_MAP.dark;
    const isOtherStore = !isBrandStore(store);

    if (isOtherStore) {
      if (storetype) {
        return themeMap[storetype] ?? themeMap.default;
      }

      if (defaultStoreIcon) {
        const base = themeMap.default;
        return {
          ...base,
          default: defaultStoreIcon,
          ...(activeStoreIcon && { active: activeStoreIcon }),
        };
      }

      return themeMap.default;
    }

    const baseIcon = (storetype ? themeMap[storetype] : undefined) ?? themeMap.default;

    if (storetype && storeTypeOverrides[storetype]) {
      return {
        ...baseIcon,
        default: storeTypeOverrides[storetype],
      };
    }

    if (defaultStoreIcon) {
      return {
        ...baseIcon,
        default: defaultStoreIcon,
        ...(activeStoreIcon && { active: activeStoreIcon }),
      };
    }

    return baseIcon;
  };
}
